import { CSSProperties } from 'react';
import {
  DASHBOARD_LAYOUT_EXCLUDED_PATHS,
  WIDGETS_LAYOUT_MARGIN_TOP,
  WIDGETS_LAYOUT_PADDING_LEFT,
  WIDGETS_LAYOUT_SIDEBAR_OFFSETS,
} from './constants';
import {
  WidgetErrorState,
  WidgetType,
} from '../../../shared/constants/widget';
import { ChartDatasetIndex } from '../../../shared/constants/chart';
import { IFilter } from '../../../shared/types/context';
import { MultiLevelBarChartTeamNode } from '../../../shared/types/widget';
import {
  DataFetchError,
  MultipleOrganisationsSelectedError,
  NoDataError,
} from './errors';

export const resolveWidgetVersion = ({
  version,
  isGroupedByTabView = false,
}: {
  version?: number;
  isGroupedByTabView?: boolean;
}) => {
  // TODO: remove isGroupedByTabView flag and related logic once all widgets in TabView are migrated to v2
  // currently widgets inside tabView are v2
  if (isGroupedByTabView) {
    return 'v2';
  }

  if (!version) {
    return 'default';
  }

  return `v${version}`;
};

const SINGLE_ORGANISATION_WIDGET_TYPES: WidgetType[] = [
  WidgetType.BENCHMARK_LINE_CHART,
  WidgetType.BENCHMARK_PROGRESS_BAR,
];

const SELF_HANDLED_NO_DATA_WIDGET_TYPES: WidgetType[] = [
  WidgetType.HERO_DIMENSION,
  WidgetType.FEEDBACK_BANNER,
  WidgetType.DIMENSION_SCORE_CARD,
  WidgetType.RECOMMENDATIONS_CARD,
];

const isMultiLevelBarChartEmpty = (
  nodes: MultiLevelBarChartTeamNode[],
): boolean =>
  nodes.every((node) => node.value === 0 && isMultiLevelBarChartEmpty(node.children));

export const assertWidgetContentRenderable = ({
  widgetType,
  content,
  appliedFilters,
}: {
  widgetType: WidgetType;
  content: any;
  appliedFilters: IFilter;
}): void => {
  if (content?.statusCode === 500) {
    throw new DataFetchError();
  }

  if (SINGLE_ORGANISATION_WIDGET_TYPES.includes(widgetType)) {
    const selectedOrganisationCount = Array.from(
      appliedFilters.selectedOrganisations.values(),
    ).filter(Boolean).length;

    if (selectedOrganisationCount > 1) {
      throw new MultipleOrganisationsSelectedError();
    }
  }

  if (SELF_HANDLED_NO_DATA_WIDGET_TYPES.includes(widgetType)) {
    return;
  }

  if (!content || content.data === null) {
    throw new NoDataError();
  }

  if (
    widgetType === WidgetType.MULTI_LEVEL_BAR_CHART &&
    Array.isArray(content.data) &&
    isMultiLevelBarChartEmpty(content.data)
  ) {
    throw new NoDataError(undefined, WidgetErrorState.EMPTY);
  }

  const datasetValues: number[] | undefined =
    content.data?.datasets?.[ChartDatasetIndex.DATA_INDEX]?.data;
  const allZero = datasetValues?.every((value) => value === 0) ?? false;
  const allNegativeOne = datasetValues?.every((value) => value === -1) ?? false;

  const isEmptyContent =
    (Array.isArray(content.data) && content.data.length === 0) ||
    (Array.isArray(content.data?.datasets) &&
      content.data.datasets.length === 0) ||
    (typeof content === 'object' && Object.keys(content).length === 0) ||
    (typeof content.data === 'object' &&
      content.data !== null &&
      !Array.isArray(content.data) &&
      Object.keys(content.data).length === 0);

  if (isEmptyContent || allZero) {
    throw new NoDataError(undefined, WidgetErrorState.EMPTY);
  }

  if (allNegativeOne) {
    throw new NoDataError();
  }
};

export const isDashboardLayoutExcludedPath = (pathname: string) =>
  DASHBOARD_LAYOUT_EXCLUDED_PATHS.some((path) => pathname.includes(path));

export const shouldShowDashboardLayout = (pathname: string) =>
  !isDashboardLayoutExcludedPath(pathname);

const resolveSidebarOffset = ({
  isMobile,
  isSidebarCollapsed,
}: {
  isMobile: boolean;
  isSidebarCollapsed?: boolean;
}) => {
  if (isMobile) {
    return WIDGETS_LAYOUT_SIDEBAR_OFFSETS.mobile;
  }

  if (isSidebarCollapsed) {
    return WIDGETS_LAYOUT_SIDEBAR_OFFSETS.collapsed;
  }

  return WIDGETS_LAYOUT_SIDEBAR_OFFSETS.expanded;
};

export const getWidgetsLayoutContentStyle = ({
  isMobile,
  isSidebarCollapsed,
}: {
  isMobile: boolean;
  isSidebarCollapsed?: boolean;
}): CSSProperties => {
  const sidebarOffset = resolveSidebarOffset({
    isMobile,
    isSidebarCollapsed,
  });

  return {
    marginTop: WIDGETS_LAYOUT_MARGIN_TOP,
    marginLeft: sidebarOffset,
    width: isMobile ? '100vw' : `calc(100vw - ${sidebarOffset}px)`,
    zIndex: 30,
    paddingLeft: isMobile
      ? WIDGETS_LAYOUT_PADDING_LEFT.mobile
      : WIDGETS_LAYOUT_PADDING_LEFT.desktop,
  };
};
